import type { BankingPaymentFilter } from 'generated/resources/graphql'

interface DateRange {
  from?: Date | string
  to?: Date | string
}

export interface PaymentsFilters {
  status?: string[]
  currency?: string[]
  date?: DateRange
  dueDate?: DateRange
}

/**
 * Converts a Date or string to ISO date string format (YYYY-MM-DD)
 */
const toIsoDateString = (value: Date | string): string => {
  if (value instanceof Date) {
    const isoString = value.toISOString()
    const datePart = isoString.split('T')[0]
    return datePart ?? isoString
  }
  return value
}

/**
 * Converts a date range to the format expected by the GraphQL filter
 * Uses 'between' operator for date ranges
 */
const getDateRange = (
  dateValue: DateRange | undefined
): { between: [string, string] } | undefined => {
  if (!dateValue?.from || !dateValue?.to) return undefined

  return {
    between: [toIsoDateString(dateValue.from), toIsoDateString(dateValue.to)],
  }
}

/**
 * Type guard to check if filters object matches PaymentsFilters interface
 */
const isPaymentsFilters = (filters: unknown): filters is PaymentsFilters => {
  if (!filters || typeof filters !== 'object') return false
  return true
}

/**
 * Builds the BankingPaymentFilter object for the GraphQL query
 * Converts UI filter values to the expected GraphQL filter structure
 */
const buildPaymentFilter = (
  filters: PaymentsFilters | undefined
): BankingPaymentFilter | undefined => {
  if (!filters) return undefined

  const filterEntries = Object.entries({
    // StringFilter with 'in' for multi-selection
    status: filters.status?.length ? { in: filters.status } : undefined,
    currency: filters.currency?.length ? { in: filters.currency } : undefined,

    // DateTimeFilter with 'between' for date ranges
    date: getDateRange(filters.date),
    dueDate: getDateRange(filters.dueDate),
  }).filter(([_, value]) => value !== undefined)

  return filterEntries.length > 0 ? Object.fromEntries(filterEntries) : undefined
}

/**
 * Maps DataCollection filter state to GraphQL query variables
 */
export const mapFiltersToVariables = (filters: Record<string, unknown> | undefined) => {
  if (!isPaymentsFilters(filters)) {
    return { filter: undefined }
  }

  return {
    filter: buildPaymentFilter(filters),
  }
}
